import { FormEvent, useCallback, useEffect, useState } from "react";

import {
  Card,
  CardHeader,
  CardContent,
  Alert,
  Divider,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { Send } from "@mui/icons-material";

import { SkillInstance } from "../../../../src/API";
import { useSkillInstances } from "../../../../providers/state/skillInstance";
import { useProfile } from "../../../../providers/profile";
import NoteMessage from "./note-message";

const Notes = ({
  instance,
  refresh,
  editable,
}: {
  instance: Partial<SkillInstance> | undefined;
  refresh: () => Promise<void>;
  editable: boolean;
}) => {
  const [notes, setNotes] = useState<
    {
      when: number;
      who: string;
      body: string;
    }[]
  >([]);
  const [noteForm, setNoteForm] = useState({ note: "" });

  const { upsertSkillInstance } = useSkillInstances();
  const { activeProfile } = useProfile();

  useEffect(() => {
    if (!instance) return;

    setNotes(JSON.parse(instance.notes || "[]"));
  }, [instance]);

  const handleNoteSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!instance || !activeProfile) return;

      const add = {
        when: new Date().valueOf(),
        who: activeProfile.name,
        body: noteForm.note,
      };
      const result = await upsertSkillInstance({
        id: instance.id,
        notes: JSON.stringify([...notes, add]),
      });
      await refresh();
      setNoteForm({ note: "" });
      return result;
    },
    [instance, activeProfile, upsertSkillInstance, noteForm, notes, refresh]
  );

  return (
    <Card>
      <CardHeader title="NOTES" sx={{ "& span": { fontSize: "1rem" } }} />
      {
        // instance may be undefined if not assigned
        instance ? (
          <CardContent>
            {notes.map((note) => (
              <div key={note.when}>
                <Divider />
                <NoteMessage note={note} />
              </div>
            ))}
            {editable ? (
              <div>
                <Divider />
                <form onSubmit={handleNoteSubmit}>
                  <div className="flex flex-col items-end gap-2 px-4 mt-4">
                    <TextField
                      required
                      value={noteForm.note}
                      onChange={(e) =>
                        setNoteForm({ ...noteForm, note: e.target.value })
                      }
                      variant="outlined"
                      sx={{
                        width: "100%",
                      }}
                      size="small"
                    />
                    <LoadingButton
                      type="submit"
                      endIcon={<Send />}
                      loading={false}
                      loadingPosition="end"
                      disabled={!noteForm.note}
                      variant="contained"
                      sx={{ maxWidth: "max-content" }}
                      size="small"
                    >
                      Send
                    </LoadingButton>
                  </div>
                </form>
              </div>
            ) : null}
          </CardContent>
        ) : (
          <CardContent>
            <Alert severity="info" sx={{ fontWeight: 700 }}>
              Available when assigned to student.
            </Alert>
          </CardContent>
        )
      }
    </Card>
  );
};

export default Notes;
